import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLang } from '../../lib/lang';
import { PAGE_KEYS } from '../../content/routes';

const NAV_KEYS = ['about', 'power', 'dc', 'ee', 'service', 'refs', 'partners', 'contact'];

export const Wordmark = ({ className = 'h-20 md:h-32' }) => (
  <img
    src="/brand/eds-angola-logo.png"
    alt="EDS Angola — Engineering & Data Solutions"
    data-testid="brand-wordmark"
    className={`${className} w-auto`}
  />
);

const LangSwitch = ({ lang, pageKey, link, dark = false }) => (
  <div className="flex items-center border border-white/20 text-[10px] font-mono font-bold tracking-[0.15em]" data-testid="lang-switch">
    {['pt', 'en'].map((l) => (
      <Link
        key={l}
        to={link(pageKey, l)}
        data-testid={`lang-switch-${l}`}
        className={`px-3 py-2 uppercase transition-colors duration-300 ${
          lang === l ? 'bg-volt text-black' : dark ? 'text-zinc-400 hover:text-white' : 'text-zinc-400 hover:text-volt'
        }`}
      >
        {l}
      </Link>
    ))}
  </div>
);

export default function Header() {
  const { lang, pageKey, t, link } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pageKey, lang]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled ? 'bg-obsidian/75 backdrop-blur-xl border-white/10' : 'bg-transparent border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-28 md:h-40 flex items-center gap-6">
          <Link to={link('home')} data-testid="nav-logo" aria-label="EDS Angola">
            <Wordmark />
          </Link>
          <div className="flex items-center gap-x-6 ml-auto">
          <nav className="hidden xl:flex items-center gap-x-5" aria-label="Primary">
            {NAV_KEYS.map((k) => (
              <Link
                key={k}
                to={link(k)}
                data-testid={`nav-link-${k}`}
                className={`font-mono text-[10px] font-bold uppercase tracking-[0.12em] whitespace-nowrap transition-colors duration-300 ${
                  pageKey === k ? 'text-volt' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {t.nav[k]}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <LangSwitch lang={lang} pageKey={pageKey} link={link} />
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="xl:hidden w-10 h-10 flex items-center justify-center border border-white/20 text-white hover:border-volt hover:text-volt transition-colors duration-300"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-obsidian/97 backdrop-blur-2xl blueprint"
          >
            <div className="h-full max-w-7xl mx-auto px-6 pt-32 pb-10 flex flex-col overflow-y-auto">
              <nav className="flex-1 flex flex-col gap-1" aria-label="Mobile">
                {['home', ...NAV_KEYS].map((k, i) => (
                  <motion.div
                    key={k}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={link(k)}
                      data-testid={`mobile-nav-link-${k}`}
                      className="group flex items-baseline gap-4 py-2 border-b border-white/5"
                    >
                      <span className="font-mono text-[10px] text-volt">0{i + 1}</span>
                      <span className={`font-display text-3xl md:text-4xl font-black uppercase tracking-tight transition-colors duration-300 ${
                        pageKey === k ? 'text-volt' : 'text-white group-hover:text-volt'
                      }`}>
                        {t.nav[k]}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="pt-8 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t.header.tagline}</p>
                <LangSwitch lang={lang} pageKey={pageKey} link={link} dark />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
